/**
 * The dashboard's company lists (SF-5095): the month's top 10 companies by total feedback and
 * the companies whose feedback fell furthest against last month.
 */
import { Table } from 'antd';
import { CompanyLink } from '../../../components/usage/CompanyLink';
import type { UsageCompanyRow, UsageDropRow } from '../../../components/usage/usageClient';
import { surveysTotal } from '../../../components/usage/usageNumbers';
import { formatNumber, formatPercent, percentChange } from '../../../utils/format';
import { salesChannelLabel } from '../../companies/constants';

const right = { align: 'right' as const };

export const TopCompaniesTable: React.FC<{ rows: UsageCompanyRow[] }> = ({ rows }) => (
  <Table<UsageCompanyRow>
    size="small"
    rowKey="company_id"
    pagination={false}
    dataSource={rows}
    columns={[
      {
        title: 'Company',
        key: 'name',
        render: (_, row) => <CompanyLink companyId={row.company_id} name={row.name} />,
      },
      {
        title: 'Sales channel',
        key: 'sales_channel',
        render: (_, row) => salesChannelLabel(row.sales_channel),
      },
      {
        title: 'Total feedback',
        key: 'total',
        ...right,
        render: (_, row) => formatNumber(row.totals.total_feedback),
      },
      {
        title: 'Surveys',
        key: 'surveys',
        ...right,
        render: (_, row) => formatNumber(surveysTotal(row.totals)),
      },
      {
        title: 'Old style ratings',
        key: 'ratings',
        ...right,
        render: (_, row) => formatNumber(row.totals.ratings),
      },
    ]}
  />
);

export const BiggestDropsTable: React.FC<{ rows: UsageDropRow[] }> = ({ rows }) => (
  <Table<UsageDropRow>
    size="small"
    rowKey="company_id"
    // The API sends every company that meets the drop rule (at most one row per customer), so
    // a bad month could list many: page them here.
    pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
    dataSource={rows}
    columns={[
      {
        title: 'Company',
        key: 'name',
        render: (_, row) => <CompanyLink companyId={row.company_id} name={row.name} />,
      },
      {
        title: 'Last month',
        key: 'previous',
        ...right,
        render: (_, row) => formatNumber(row.previous_total_feedback),
      },
      {
        title: 'This month',
        key: 'current',
        ...right,
        render: (_, row) => formatNumber(row.total_feedback),
      },
      {
        title: 'Change',
        key: 'change',
        ...right,
        // Worked out from the two counts so the shown figure never depends on whether the API
        // sends the change as a percent or a fraction.
        render: (_, row) =>
          formatPercent(percentChange(row.total_feedback, row.previous_total_feedback), 0),
      },
    ]}
  />
);
